import type { SnowflakeId } from '@/types/snowflake'

/**
 * 有多个虚拟地点的人动物料时，后端回 VIRTUAL_LOCATION_REQUIRED，要选一个再重发（PRD §8）。
 *
 * 判断放在 taskExpenseApi，不放在各个入口：动作有七八个入口，失败时大多自己 toast、不抛，
 * 按入口接要改七八处，漏一处就是死路。入口拿到的还是一次成功或失败。
 * 选择框挂在 Layout 上，用这个登记点接到 API 层，API 层不 import 组件。
 */
export type VirtualPlaceResolver = (listingIds?: string[]) => Promise<SnowflakeId | null>

let resolver: VirtualPlaceResolver | null = null

export function registerVirtualPlaceResolver(next: VirtualPlaceResolver): () => void {
  resolver = next
  return () => {
    if (resolver === next) resolver = null
  }
}

/** 回选中的 id。用户取消、没有挂选择框、取候选失败时回 null */
export function askVirtualPlace(listingIds?: string[]): Promise<SnowflakeId | null> {
  return resolver ? resolver(listingIds).catch(() => null) : Promise.resolve(null)
}

export function isVirtualLocationRequired(err: unknown): boolean {
  return (err as { code?: unknown } | null | undefined)?.code === 'VIRTUAL_LOCATION_REQUIRED'
}

/** 后端在错误 details 里列候选。per-row 错误不过 schema，是 snake_case */
export function virtualPlaceChoices(details: unknown): string[] | undefined {
  const ids = (details as { listing_ids?: unknown } | null | undefined)?.listing_ids
  return Array.isArray(ids) ? ids.map(String) : undefined
}

/**
 * 一次提交里多行都要问时只问一次（同手机 createVirtualRetry）：并发的几行拿同一个答案。
 * 取消（null）也共用 —— 用户已经说了不选，不该再弹第二次。
 */
export function sharedVirtualAsk(
  ask: VirtualPlaceResolver = askVirtualPlace,
): VirtualPlaceResolver {
  let pending: Promise<SnowflakeId | null> | null = null
  return (listingIds) => (pending ??= ask(listingIds))
}
